import React, {useCallback, useMemo, memo} from 'react';
import {useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {GuestStatusesTexts} from 'components/GuestsPanel/types';
import {useAuth} from 'hooks/auth/useAuth';
import {usePath, useErrorModal, useModalControls, useTimeoutRef} from 'hooks';
import {useDeleteGuest} from 'hooks/useDeleteGuest';
import {useIVStore} from 'store/identityVerification';
import {useIsEditableMember} from 'components/GuestsPanel/useIsEditableMember';
import {GUEST_STATUSES, ShortGuest} from '@guestapp/sdk';
import {IS_LEADER_GUEST_STORAGE_KEY} from 'utils/constants';
import {PATHS} from 'Routes';
import completeIcon from '../../../../assets/icons/complete-icon.svg?no-inline';
import incompleteIcon from '../../../../assets/icons/incomplete-icon.svg?no-inline';
import Dropdown from 'components/common/Dropdown';
import IconButton from 'components/common/IconButton';
import {DeleteGuestStatusModal} from './DeteleGuestStatusModal';
import {GuestPanelItem} from '../GuestPanelItem';
import {Texts, Title, StatusText, StatusIcon, DotsButton} from './styled';

const guestStatusText: GuestStatusesTexts = {
  [GUEST_STATUSES.complete]: 'completed',
  [GUEST_STATUSES.verification_pending]: 'verification_pending',
  [GUEST_STATUSES.incomplete]: 'registration_pending',
};

const DELETE_GUEST_MODAL_TIMEOUT = 3000;

type Props = {
  guestMember: ShortGuest & {isLeader?: boolean};
  isLoading?: boolean;
};
const GuestItem = memo(({guestMember, isLoading}: Props) => {
  const {getMainPath} = usePath();
  const {resetAllIVState} = useIVStore();
  const {ErrorModal, displayError} = useErrorModal();
  const navigate = useNavigate();
  const {isLoggedIn} = useAuth();
  const closeModalTimeoutRef = useTimeoutRef();
  const {isOpen, openModal, closeModal} = useModalControls();
  const {t} = useTranslation();
  const {mutate: deleteGuestMutate, status: deleteGuestStatus} = useDeleteGuest({
    onError: displayError,
    onMutate: openModal,
    onSuccess: () => {
      closeModalTimeoutRef.current = setTimeout(() => {
        closeModal();
      }, DELETE_GUEST_MODAL_TIMEOUT);
    },
  });

  const enableGuestsRemoval =
    window?.ChekinProSettings?.enableGuestsRemoval || isLoggedIn;
  const {full_name, guestapp_status, isLeader} = guestMember;
  const completed = guestapp_status === GUEST_STATUSES.complete;
  const statusIcon = completed ? completeIcon : incompleteIcon;
  const statusText = guestStatusText[guestapp_status] || '';

  const {isEditable} = useIsEditableMember({member: guestMember});

  const getLinkToUpdateGuestProcessView = useCallback(
    (guest?: ShortGuest) => {
      const guestId = guest?.id;
      const guestStatus = guest?.guestapp_status;
      const isVerificationPending = guestStatus === GUEST_STATUSES.verification_pending;
      const isPreRegisteredAndInCompleted =
        guest?.is_pre_registered && guestStatus === GUEST_STATUSES.incomplete;
      if (guestId) {
        if (isVerificationPending) {
          return getMainPath(`${PATHS.verification.main}/${guestId}`);
        }
        if (isPreRegisteredAndInCompleted || isLoggedIn) {
          const isLeaderFlag = `${IS_LEADER_GUEST_STORAGE_KEY}=${isLeader}`;
          return getMainPath(
            `${PATHS.addPersonalDataForm}/${guestId}${
              isLeader ? '?' + isLeaderFlag : ''
            }`,
          );
        }
      }
    },
    [getMainPath, isLeader, isLoggedIn],
  );

  const gotToEdit = useCallback(() => {
    const link = getLinkToUpdateGuestProcessView(guestMember);

    if (isEditable && link) {
      resetAllIVState();
      navigate(link);
    }
  }, [
    getLinkToUpdateGuestProcessView,
    guestMember,
    isEditable,
    navigate,
    resetAllIVState,
  ]);

  const handleDelete = useCallback(
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      e.preventDefault();
      if (enableGuestsRemoval && guestMember.id) {
        deleteGuestMutate(guestMember.id);
      }
    },
    [deleteGuestMutate, enableGuestsRemoval, guestMember.id],
  );

  const dropdownOptions = useMemo(() => {
    return [
      // {
      //   id: 1,
      //   label: t('fill_in_incomplete_data'),
      //   onClick: () => {
      //     console.log('remove item');
      //   },
      // },
      {
        id: 2,
        label: t('edit_guest'),
        onClick: gotToEdit,
        hidden: !isEditable,
      },
      {
        id: 3,
        label: t('delete_guest'),
        onClick: handleDelete,
        hidden: !enableGuestsRemoval,
      },
    ];
  }, [enableGuestsRemoval, gotToEdit, handleDelete, isEditable, t]);

  const showActions = dropdownOptions.some(action => !action.hidden);

  return (
    <GuestPanelItem
      $isLoading={isLoading}
      $completed={completed}
      className="guests-panel__item"
    >
      <Texts>
        <Title className="guests-panel__title">{full_name}</Title>
        <StatusText className="guests-panel__status" $completed={completed}>
          <StatusIcon src={statusIcon} width={14} />
          {t(statusText)}
        </StatusText>
      </Texts>
      {showActions && (
        <Dropdown
          position="bottom right"
          options={dropdownOptions}
          disabled={isLoading}
          trigger={
            <div>
              <IconButton icon={<DotsButton name="dots" size={16} />} />
            </div>
          }
        />
      )}
      <ErrorModal />
      <DeleteGuestStatusModal open={isOpen} state={deleteGuestStatus} />
    </GuestPanelItem>
  );
});

export {GuestItem};
